import React from 'react';
import { LayerGroup, LayersControl, TileLayer } from 'react-leaflet';

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
// The OSM tile server has no tiles past zoom 19; beyond that Leaflet upscales
// the zoom-19 tiles instead of requesting levels that come back empty.
const OSM_MAX_NATIVE_ZOOM = 19;

// OpenTopoMap renders no tiles past zoom 17, and its contour lines come from
// a ~30 m elevation model that has nothing more to show at closer range anyway.
const OPEN_TOPO_MAP_MAX_ZOOM = 17;

/**
 * The base maps offered on the region maps. OpenStreetMap stays the default;
 * Esri World Imagery is the aerial/satellite alternative. Note that the Esri
 * tiles are not open data -- they are served under Esri's own terms.
 *
 * "OpenTopoMap" hands over to the plain OpenStreetMap tiles once the map is
 * zoomed in past OPEN_TOPO_MAP_MAX_ZOOM and back again when zooming out. Both
 * tile layers sit in one group behind the same radio button, each limited to
 * its own zoom range -- Leaflet shows a tile layer only within its
 * minZoom/maxZoom, so the switch needs no zoom listener, and the radio button
 * stays on "OpenTopoMap" throughout.
 */
const BaseLayers = ({ maxZoom = 21 }) => (
  <LayersControl position="topright">
    <LayersControl.BaseLayer checked name="OpenStreetMap">
      <TileLayer
        maxZoom={maxZoom}
        maxNativeZoom={OSM_MAX_NATIVE_ZOOM}
        url={OSM_URL}
        attribution={OSM_ATTRIBUTION}
      />
    </LayersControl.BaseLayer>

    <LayersControl.BaseLayer name="OpenTopoMap">
      <LayerGroup>
        <TileLayer
          maxZoom={OPEN_TOPO_MAP_MAX_ZOOM}
          url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
          attribution={
            'Kartendaten: ' + OSM_ATTRIBUTION + ', SRTM | Kartendarstellung: &copy; '
            + '<a href="https://opentopomap.org">OpenTopoMap</a> '
            + '(<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
          }
        />
        <TileLayer
          minZoom={OPEN_TOPO_MAP_MAX_ZOOM + 1}
          maxZoom={maxZoom}
          maxNativeZoom={OSM_MAX_NATIVE_ZOOM}
          url={OSM_URL}
        />
      </LayerGroup>
    </LayersControl.BaseLayer>

    <LayersControl.BaseLayer name="Satellit">
      <TileLayer
        maxZoom={maxZoom}
        // The service has no tiles past zoom 19; without maxNativeZoom
        // Leaflet would request levels it cannot answer and the deepest
        // zooms would come back empty instead of upscaled.
        maxNativeZoom={19}
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        attribution="Esri, Maxar, Earthstar Geographics"
      />
    </LayersControl.BaseLayer>
  </LayersControl>
);

export default BaseLayers;
